import {
  type ApolloError,
  type ApolloQueryResult,
  type DocumentNode,
  useQuery,
} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type {
  DimensionsCollection,
  PanelApplication,
  PanelDegreeRankings,
  PanelDimensionalScores,
  PanelMember,
  PanelRemarks,
  ScholarshipScores,
} from './types';
import type { PANEL_STATUS, PanelFormData } from '../GSManagement';
import {
  defaultAppliedFilter,
  getMergeFilters,
  PANEL_RESULT,
  type PanelAppliedFilter,
  type PanelLockedFilter,
  panelResultLabelMapping,
} from '../../../GSAssessment';
import { PublicationType, RankingSystem } from '../../../DRCAssessment';
import { panelOptionWithMixedAbbv } from '../../../../Nomination/components/';
import {
  FundingPanel,
  FundingSource,
} from '../../../../Nomination/components/NominationForm/FundingModule/types';
import { getOptionsLabel } from '../../../../Monitor/components';
import {
  ApplicantPublicationRole,
  type AWARD_CLASSIFICATION,
  awardClassificationOptions,
  levelOfDegreeOptions,
  PublicationStatus,
} from '../../../../Application/components';
import { UserRole, type SelectOption } from '../../../../../components';
import { type ProgrammeTypes } from '../../../../Nomination';
import { FUNDING_STATUS } from '../../../../ScholarshipManagement/constants/fundingStatus';

type PanelDetailNumber = {
  totalNumber: number;
  scienceNumber: number;
  humanityNumber: number;
};
export type PanelNumbers = {
  panelDetailNumber: PanelDetailNumber;
  panelShortlistNumber: PanelDetailNumber;
  panelShortlistOutstandingNumber: PanelDetailNumber;
  panel3YearNumber: PanelDetailNumber;
  panel2YearNumber: PanelDetailNumber;
  panel1YearNumber: PanelDetailNumber;
  panelWithdrawnNumber: number;
  panelFailedNumber: number;
  totalStudentshipYears: number;
};

type GetPanelApplications = {
  panelApplications: PanelApplication[];
  panelInformation: PanelFormData;
  panelNumbers: PanelNumbers;
  dimensionsCollection: DimensionsCollection[];
  allocationReleased: boolean;
  hasAllocatedReviewers: boolean;
  dualPartnerUniversityList: SelectOption[];
  lockFilterControllers: {
    filterLockedFlag: boolean;
    filterToApply: PanelAppliedFilter;
  };

  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

type PublicationDataFromApplication = {
  _id: string;
  applicantRole?: ApplicantPublicationRole;
  publicationName: string;
  publicationStatus?: PublicationStatus;
};

type PublicationDataFromEvaluation = {
  _id: string;
  type: PublicationType;
};
const getQuery = (onlyReviewee: boolean): DocumentNode => {
  const listApi = onlyReviewee
    ? 'getAssignedPanelApplications(panelId: $panelId)'
    : 'getPanelApplications(panelId: $panelId,filter:$filter)';

  const GET_PANEL_APPLICATIONS = gql`
  query ($panelId: ID!, ${onlyReviewee ? '' : '$filter:PanelApplicationFilter,'} $panelFilter: PanelBaseFilter) {
    programmeChoiceMetadata {
      countryList {
        value
        label
      }
      rpgJoinPhdPartnerList{
        value
        label
      }
    }
    scholarshipNominationMetadata {
      dimensions {
        dimensionName
        abbv
      }
    }

    panelSummaryTable(panelId: $panelId) {
      totalStudentshipYears
    }

    ${listApi} {
      serialNo
      applicationId
      applicationNo
      applicantName
      department
      partnerUniversity
      classification
      tags
      status
      isTop10
      nationality
      panelResult
      csStudentshipYears
      csRemainStudentshipYears
      deptPriorityNumber
      remarks {
        netId
        remark
        role
      }
      nomination {
        admission{
          programme
        }
        fundings {
          status
          source
        }
      }
      firstDegree {
        name
        degreeLevel
        gpa
        currentStudy
        originalGPA
        originalGPAOutOf
        classRanking
        classRankingOutOf
        awardClassification
        rankings {
          rankingSystem
          originalRanking
          universityLevel
        }
      }
      secondDegree {
        name
        degreeLevel
        gpa
        currentStudy
        originalGPA
        originalGPAOutOf
        classRanking
        classRankingOutOf
        awardClassification
        rankings {
          rankingSystem
          originalRanking
          universityLevel
        }
      }
      publications {
        _id
        publicationName
        applicantRole
        publicationStatus
      }
      merits {
        meritName
      }
      scholarshipNomination {
        scores {
          funding
          score
          systemScore
        }
        subjectClassification
        researchAssessments {
          type
          _id
        }

        dimensionalScores {
          dimension
          systemScore
          overrideScore
          scoringSystem
          remark
        }
      }
      avgReviewerScore
      avgScore
      reviewers {
        netId
        comment
        scoresOverride {
        totalScore
          funding
          AE
          AEComment
          RP
          RPComment
          LS
          LSComment
          CI
          CIComment
          RE
          REComment
          OS
          OSComment
          CD
          CDComment
          US
          USComment
        }
        declaration
      }
      remarks {
        netId
        remark
        role
      }
    }

    panel(filter: $panelFilter) {
      allocationReleased
      panelMembers {
        netId
        jobTitle
        email
        givenName
        surname
        department
        institution
        classification
        isPanelChair
      }
      panelNo
      fundingSource
      panelNo
      title
      panelRound
      panelChair {
        netId
      }
      gsHelper
      panelType
      startDate
      endDate
      status
      tagOptions
      resultOptions
      lockFilter
    }
  }
`;

  return GET_PANEL_APPLICATIONS;
};

const classificationAbbreviation: Record<FundingPanel, string> = {
  [FundingPanel.SCIENCES_MEDICINE_ENGINEERING_TECHNOLOGY]: 'S',
  [FundingPanel.HUMANITIES_SOCIAL_SCIENCES_BUSINESS_STUDIES]: 'H',
  [FundingPanel.MIXED]: 'M',
};

export const useGetPanelApplications = (getPanelApplicationsInputs: {
  panelId: string;
  onlyReviewee: boolean;
  inputFiler?: PanelAppliedFilter;
  filter?: any;
  initialLocked?: boolean;
}): GetPanelApplications => {
  const {
    panelId,
    onlyReviewee,
    filter, // json filter
    inputFiler = defaultAppliedFilter,
    initialLocked = false,
  } = getPanelApplicationsInputs;
  const { data, loading, error, refetch } = useQuery(getQuery(onlyReviewee), {
    variables: {
      panelId,
      ...(onlyReviewee ? {} : { filter: filter ?? {} }), // getAssignedPanelApplications does not support filtering
      panelFilter: {
        _id: { eq: panelId },
      },
    },
    fetchPolicy: 'network-only',
    skip: !panelId,
  });
  const nationalityOptions: SelectOption[] =
    data?.programmeChoiceMetadata?.countryList ?? [];
  const dualPartnerUniversityList: SelectOption[] =
    data?.programmeChoiceMetadata?.rpgJoinPhdPartnerList ?? [];
  const dimensionsCollection: DimensionsCollection[] =
    data?.scholarshipNominationMetadata?.dimensions ?? [];

  const applications =
    (data?.[
      onlyReviewee ? 'getAssignedPanelApplications' : 'getPanelApplications'
    ] as any[]) ?? [];

  let panelApplications: PanelApplication[] = [];
  let panelNumbers: PanelNumbers = {
    panelDetailNumber: {
      totalNumber: 0,
      scienceNumber: 0,
      humanityNumber: 0,
    },
    panelShortlistNumber: {
      totalNumber: 0,
      scienceNumber: 0,
      humanityNumber: 0,
    },
    panelShortlistOutstandingNumber: {
      totalNumber: 0,
      scienceNumber: 0,
      humanityNumber: 0,
    },
    panel3YearNumber: {
      totalNumber: 0,
      scienceNumber: 0,
      humanityNumber: 0,
    },
    panel2YearNumber: {
      totalNumber: 0,
      scienceNumber: 0,
      humanityNumber: 0,
    },
    panel1YearNumber: {
      totalNumber: 0,
      scienceNumber: 0,
      humanityNumber: 0,
    },
    panelWithdrawnNumber: 0,
    panelFailedNumber: 0,
    totalStudentshipYears: 0,
  };
  const rawData = data?.panel ?? {};
  const rawPanelMember = (rawData?.panelMembers as any[]) ?? [];

  const panelChairNetId =
    (rawPanelMember as PanelMember[]).find(member => member.isPanelChair)
      ?.netId ?? '';

  const sortedPanelMembers = [...rawPanelMember]?.sort(
    (a: PanelMember, b: PanelMember) =>
      Number(b.isPanelChair) - Number(a.isPanelChair),
  );

  const panelMember: PanelMember[] = sortedPanelMembers?.map(
    (member: PanelMember, index: number) => {
      const {
        netId,
        jobTitle,
        email,
        givenName,
        surname,
        department,
        institution,
        classification,
        isPanelChair,
      } = member;

      return {
        order: index,
        netId,
        title: '', //  this field is required in input, keep it just for consistency
        name: `${surname.toUpperCase()} ${givenName}`,
        jobTitle,
        email,
        givenName,
        surname,
        department,
        institution,
        classification,
        isPanelChair,
      };
    },
  );

  const filterLockedFlag = Boolean(rawData?.lockFilter);

  // string -> json
  const jsonFilter = filterLockedFlag
    ? JSON.parse(rawData?.lockFilter ?? `{}`)
    : undefined;

  const lockFilter: PanelLockedFilter = {
    tags: jsonFilter?.tags?.all,
    panelResults: jsonFilter?.panelResults?.all,
    priorityOrderRange: jsonFilter?.priorityRange
      ? {
          min: jsonFilter?.priorityRange?.[0] ?? 0,
          max: jsonFilter?.priorityRange?.[1] ?? 999,
        }
      : undefined,
    serialNo: (jsonFilter?.serialNo as number) ?? undefined,
    expr: jsonFilter?.expr ?? undefined,
    partnerUniversity: jsonFilter?.partnerUniversity ?? undefined,
  };

  const panelInformation: PanelFormData = {
    panelNo: rawData?.panelNo ?? '',
    _id: panelId,
    title: rawData?.title ?? '',
    fundingSource: rawData?.fundingSource,
    entryYear: rawData?.fundingSource ?? '',
    panelType: (rawData?.panelType as FundingPanel) ?? '',
    endDate: rawData?.endDate ?? null,
    panelChair: panelChairNetId,
    tagOptions: rawData?.tagOptions ?? [],
    panelMember,
    status: rawData?.status as PANEL_STATUS,
    resultOptions: (rawData?.resultOptions ?? []).map((op: PANEL_RESULT) => ({
      value: op,
      label: panelResultLabelMapping[op],
    })),
    lockFilter,
  };

  const rankingOrder = [
    RankingSystem.QS,
    RankingSystem.THE,
    RankingSystem.ARWU,
  ];

  let hasAllocatedReviewers = false;

  if (!loading) {
    const applicationScienceTotal = applications?.filter(
      applicant =>
        applicant?.scholarshipNomination?.subjectClassification ===
        FundingPanel.SCIENCES_MEDICINE_ENGINEERING_TECHNOLOGY,
    );

    const applicationHumanityTotal = applications?.filter(
      applicant =>
        applicant?.scholarshipNomination?.subjectClassification ===
        FundingPanel.HUMANITIES_SOCIAL_SCIENCES_BUSINESS_STUDIES,
    );

    const getPanelResultNumber = (
      panelResult: PANEL_RESULT,
    ): PanelDetailNumber => {
      return {
        totalNumber: applications?.filter(
          applicant => applicant?.panelResult === panelResult,
        ).length,
        scienceNumber: applicationScienceTotal.filter(
          applicant => applicant?.panelResult === panelResult,
        ).length,
        humanityNumber: applicationHumanityTotal.filter(
          applicant => applicant?.panelResult === panelResult,
        ).length,
      };
    };

    panelNumbers = {
      panelDetailNumber: {
        totalNumber: applications?.length,
        scienceNumber: applicationScienceTotal.length,
        humanityNumber: applicationHumanityTotal.length,
      },
      panelShortlistNumber: getPanelResultNumber(PANEL_RESULT.SHORT_LISTED),
      panelShortlistOutstandingNumber: getPanelResultNumber(
        PANEL_RESULT.SHORT_LISTED_OUTSTANDING,
      ),
      panel3YearNumber: getPanelResultNumber(PANEL_RESULT.THREE_YEARS),
      panel2YearNumber: getPanelResultNumber(PANEL_RESULT.TWO_YEARS),
      panel1YearNumber: getPanelResultNumber(PANEL_RESULT.ONE_YEAR),

      panelWithdrawnNumber: applications?.filter(
        applicant => applicant?.panelResult === PANEL_RESULT.WITHDRAWN,
      ).length,
      panelFailedNumber: applications.filter(
        applicant => applicant?.panelResult === PANEL_RESULT.FAILED,
      ).length,

      totalStudentshipYears:
        data?.panelSummaryTable?.totalStudentshipYears ?? 0,
    };

    panelApplications = applications.map(application => {
      const {
        applicationId = '',
        applicationNo = '',
        applicantName = '',
        department = '',
        partnerUniversity: partnerUni = '',
        csStudentshipYears = 0,
        csRemainStudentshipYears = 0,
        deptPriorityNumber = 0,
        reviewers = [],
        avgReviewerScore = 0,
        avgScore = 0,
        tags = [],
        firstDegree,
        nomination,
        secondDegree,
        publications = [],
        scholarshipNomination,
        panelResult = PANEL_RESULT.TBD,
        isTop10 = false,
        nationality,
        merits = [],
        remarks = [],
        serialNo,
      } = application ?? {};
      // any panel application got reviewers, means reviewers have been allocated
      hasAllocatedReviewers = (reviewers ?? []).length > 0;
      const scholarshipScores = (scholarshipNomination?.scores ??
        []) as ScholarshipScores[];

      const panelDimensionalScores =
        scholarshipNomination?.dimensionalScores as PanelDimensionalScores[];

      const publicationAssessment =
        (scholarshipNomination?.researchAssessments as PublicationDataFromEvaluation[]) ??
        [];

      const qOneIds: string[] = publicationAssessment
        .filter(
          pub =>
            pub?.type === PublicationType.JCR_Q1 ||
            pub?.type === PublicationType.JCR_Q1_EQUIVALENT,
        )
        .map(pub => pub._id);

      // show 1st Author / 2nd Author / Corresponding Author only
      // show Published only
      const Q1paperNames: string[] = (
        publications as PublicationDataFromApplication[]
      )
        .filter(
          pub =>
            qOneIds.includes(pub._id) &&
            pub?.applicantRole != null &&
            pub?.applicantRole !== ApplicantPublicationRole.OTHER &&
            pub?.publicationStatus === PublicationStatus.PUBLISHED,
        )
        .map(pub => pub.publicationName);

      const qTwoIds: string[] = publicationAssessment
        .filter(
          pub =>
            pub?.type === PublicationType.JCR_Q2 ||
            pub?.type === PublicationType.JCR_Q2_EQUIVALENT,
        )
        .map(pub => pub._id);

      const Q2paperNames: string[] = (
        publications as PublicationDataFromApplication[]
      )
        .filter(
          pub =>
            qTwoIds.includes(pub._id) &&
            pub?.applicantRole != null &&
            pub?.applicantRole !== ApplicantPublicationRole.OTHER &&
            pub?.publicationStatus === PublicationStatus.PUBLISHED,
        )
        .map(pub => pub.publicationName);

      const isHKPFSNominee: boolean = (nomination?.fundings ?? [])?.some(
        (funding: any) =>
          funding?.source === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME &&
          funding?.status === FUNDING_STATUS.GS_TO_RGC,
      );
      const offeredProgramme = nomination?.admission
        ?.programme as ProgrammeTypes;

      return {
        serialNo,
        applicationId,
        applicationNo,
        applicantName,
        isHKPFSNominee,
        department,
        partnerUniversity: getOptionsLabel(
          dualPartnerUniversityList,
          partnerUni,
        ),
        nationality: getOptionsLabel(nationalityOptions, nationality),
        csStudentshipYears: csStudentshipYears ?? 0, // in case its null
        csRemainStudentshipYears: csRemainStudentshipYears ?? 0, // in case its null
        deptPriorityNumber,
        firstDegreeInformation: {
          ...firstDegree,
          degreeLevel: getOptionsLabel(
            levelOfDegreeOptions,
            firstDegree?.degreeLevel,
          ),
          awardClassification: getOptionsLabel(
            awardClassificationOptions,
            firstDegree?.awardClassification as AWARD_CLASSIFICATION,
          ),
          // Sort the rankings based on the defined order
          rankings: [
            ...((firstDegree?.rankings as PanelDegreeRankings[]) ?? []),
          ].sort((a, b) => {
            return (
              rankingOrder.indexOf(a.rankingSystem) -
              rankingOrder.indexOf(b.rankingSystem)
            );
          }),
        },
        secondDegreeInformation: {
          ...secondDegree,
          degreeLevel: getOptionsLabel(
            levelOfDegreeOptions,
            secondDegree?.degreeLevel,
          ),
          awardClassification: getOptionsLabel(
            awardClassificationOptions,
            secondDegree?.awardClassification as AWARD_CLASSIFICATION,
          ),
          // Sort the rankings based on the defined order
          rankings: [
            ...((secondDegree?.rankings as PanelDegreeRankings[]) ?? []),
          ].sort((a, b) => {
            return (
              rankingOrder.indexOf(a.rankingSystem) -
              rankingOrder.indexOf(b.rankingSystem)
            );
          }),
        },
        scholarshipScores,
        tags,
        offeredProgramme,
        classification: getOptionsLabel(
          panelOptionWithMixedAbbv,
          (scholarshipNomination?.subjectClassification as FundingPanel) ?? '',
        ),
        reviewers: (reviewers ?? []).map((reviewer: any) => {
          const { netId, scoresOverride, declaration, comment } = reviewer;
          return {
            netId,
            name:
              panelMember.find(member => member.netId === netId)?.name ?? '',
            comment,
            department:
              panelMember.find(member => member.netId === netId)?.department ??
              '',
            scoresOverride: {
              totalScore: scoresOverride?.totalScore,
              funding: scoresOverride?.funding,
              AE: scoresOverride?.AE,
              AEComment: scoresOverride?.AEComment,
              RP: scoresOverride?.RP,
              RPComment: scoresOverride?.RPComment,
              LS: scoresOverride?.LS,
              LSComment: scoresOverride?.LSComment,
              CI: scoresOverride?.CI,
              CIComment: scoresOverride?.CIComment,
              RE: scoresOverride?.RE,
              REComment: scoresOverride?.REComment,
              OS: scoresOverride?.OS,
              OSComment: scoresOverride?.OSComment,
              CD: scoresOverride?.CD,
              CDComment: scoresOverride?.CDComment,
              US: scoresOverride?.US,
              USComment: scoresOverride?.USComment,
            },
            declaration,
            classification:
              classificationAbbreviation[
                panelMember.find(member => member.netId === netId)
                  ?.classification as FundingPanel
              ] ?? '',
          };
        }),
        avgReviewerScore,
        avgScore,
        panelResult,
        publicationData: {
          Q1: {
            num: Q1paperNames.length,
            publicationNames: Q1paperNames,
          },
          Q2: {
            num: Q2paperNames.length,
            publicationNames: Q2paperNames,
          },
        },
        isTopTen: isTop10 as boolean,
        merits: merits?.map((merit: any) => merit?.meritName),
        panelRemarks: (remarks as PanelRemarks[]).filter(
          rmk => rmk.role === UserRole.PANEL,
        ),
        panelDimensionalScores,
        remarksDorpgs: (remarks as PanelRemarks[]).filter(
          rmk => rmk.role === UserRole.DO_RPG_S,
        ),
      };
    });
  }

  const allocationReleased: boolean = rawData?.allocationReleased ?? false;

  // 1. lock status changes ->  initialLocked !== filterLockedFlag
  // should apply the locked filter from backend
  // 2. remain locked/unlocked
  // should apply merged input filter & output filter(lock filter, if any)
  const isLockChange: boolean = initialLocked !== filterLockedFlag;

  const {
    panelResults: lockedPanelResults,
    tags: lockedTags,
    ...otherLockedFields
  } = lockFilter;
  const processedLockedFiler: PanelAppliedFilter = {
    panelResults: lockedPanelResults ?? [],
    tags: lockedTags ?? [],
    ...otherLockedFields,
  };
  const filterToApply: PanelAppliedFilter = isLockChange
    ? processedLockedFiler
    : getMergeFilters(inputFiler, lockFilter);

  return {
    panelApplications,
    panelInformation,
    panelNumbers,
    hasAllocatedReviewers,
    allocationReleased,
    dimensionsCollection,
    dualPartnerUniversityList,
    lockFilterControllers: {
      filterLockedFlag,
      filterToApply,
    },
    loading,
    error,
    refetch,
  };
};
